import { useState } from 'react';
import { Send, Plus, Trash2, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface SimpleEmbed {
  title: string;
  description: string;
  color: string;
}

const WebhookSender = () => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [content, setContent] = useState('');
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [embeds, setEmbeds] = useState<SimpleEmbed[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [lastResponse, setLastResponse] = useState<{ success: boolean; status: number; message: string } | null>(null);

  const addEmbed = () => {
    setEmbeds([...embeds, { title: '', description: '', color: '#5865F2' }]);
  };

  const updateEmbed = (index: number, key: keyof SimpleEmbed, value: string) => {
    const newEmbeds = [...embeds];
    newEmbeds[index] = { ...newEmbeds[index], [key]: value };
    setEmbeds(newEmbeds);
  };

  const removeEmbed = (index: number) => {
    setEmbeds(embeds.filter((_, i) => i !== index));
  };

  const sendWebhook = async () => {
    if (!webhookUrl) {
      toast.error('Please enter a webhook URL');
      return;
    }

    if (!content && embeds.length === 0) {
      toast.error('Please add content or at least one embed');
      return;
    }

    setIsSending(true);
    setLastResponse(null);

    try {
      const payload: any = {};
      
      if (content) payload.content = content;
      if (username) payload.username = username;
      if (avatarUrl) payload.avatar_url = avatarUrl;
      
      if (embeds.length > 0) {
        payload.embeds = embeds
          .filter(e => e.title || e.description)
          .map(e => ({
            title: e.title || undefined,
            description: e.description || undefined,
            color: parseInt(e.color.replace('#', ''), 16),
          }));
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok || response.status === 204) {
        setLastResponse({
          success: true,
          status: response.status,
          message: 'Webhook sent successfully!',
        });
        toast.success('Webhook sent successfully!');
      } else {
        const errorText = await response.text();
        setLastResponse({
          success: false,
          status: response.status,
          message: errorText || `Error: ${response.statusText}`,
        });
        toast.error(`Failed to send webhook: ${response.status}`);
      }
    } catch (error: any) {
      setLastResponse({
        success: false,
        status: 0,
        message: error.message || 'Network error',
      });
      toast.error('Failed to send webhook');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Send className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Webhook Sender</h1>
          <p className="text-muted-foreground">
            Send messages to Discord webhooks
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Form */}
        <div className="space-y-4">
          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Webhook Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Webhook URL *</Label>
                <Input
                  type="url"
                  placeholder="https://discord.com/api/webhooks/..."
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Username (optional)</Label>
                  <Input
                    placeholder="Custom bot name"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Avatar URL (optional)</Label>
                  <Input
                    placeholder="Custom avatar image"
                    value={avatarUrl}
                    onChange={(e) => setAvatarUrl(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Message Content</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Message content (supports markdown)"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-[100px]"
              />
            </CardContent>
          </Card>

          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Embeds</span>
                <Button variant="outline" size="sm" onClick={addEmbed} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Add Embed
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {embeds.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No embeds added yet
                </p>
              ) : (
                embeds.map((embed, index) => (
                  <div key={index} className="p-3 rounded-lg bg-muted/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Embed {index + 1}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => removeEmbed(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Input
                          placeholder="Title"
                          value={embed.title}
                          onChange={(e) => updateEmbed(index, 'title', e.target.value)}
                        />
                      </div>
                      <div className="flex gap-1">
                        <Input
                          type="color"
                          value={embed.color}
                          onChange={(e) => updateEmbed(index, 'color', e.target.value)}
                          className="w-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={embed.color}
                          onChange={(e) => updateEmbed(index, 'color', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <Textarea
                      placeholder="Description"
                      value={embed.description}
                      onChange={(e) => updateEmbed(index, 'description', e.target.value)}
                      className="min-h-[60px]"
                    />
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Send & Response */}
        <div className="space-y-4">
          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Send Webhook</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={sendWebhook}
                disabled={isSending || !webhookUrl}
                className="w-full gap-2"
                size="lg"
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {isSending ? 'Sending...' : 'Send Webhook'}
              </Button>

              {/* Response Display */}
              {lastResponse && (
                <div className={`p-4 rounded-lg ${lastResponse.success ? 'bg-green-500/10' : 'bg-destructive/10'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {lastResponse.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-destructive" />
                    )}
                    <span className={`font-medium ${lastResponse.success ? 'text-green-500' : 'text-destructive'}`}>
                      {lastResponse.success ? 'Success' : 'Failed'}
                    </span>
                    <Badge variant="outline">
                      Status: {lastResponse.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {lastResponse.message}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Payload Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="p-4 rounded-lg bg-muted/30 overflow-auto text-xs font-mono max-h-[300px]">
                {JSON.stringify(
                  {
                    content: content || undefined,
                    username: username || undefined,
                    avatar_url: avatarUrl || undefined,
                    embeds: embeds.length > 0 
                      ? embeds.filter(e => e.title || e.description).map(e => ({
                          title: e.title || undefined,
                          description: e.description || undefined,
                          color: parseInt(e.color.replace('#', ''), 16),
                        }))
                      : undefined,
                  },
                  null,
                  2
                )}
              </pre>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WebhookSender;